/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable no-underscore-dangle */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useMutation, useQuery } from '@apollo/client';
import { getCurrentUserHook, getTranslatedText } from '@nile/components/src';
import {
  AccountClient,
  GET_PERSONALIZATION_BY_NAME,
  SAVE_PERSONALIZATION
} from '@nile/graphql';
import { noop } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import Tippy from '@tippyjs/react';
import { DisplayText } from '@superops/ui-design-system';

const SIZE = 80;
const INTERCOM_Z_INDEX = 2147483000;

function getDefaultPosition(size = SIZE) {
  const LEFT_NAV_PANE_WIDTH = 75;

  const FLOATER_DEFAULT_MARGIN = { LEFT: 10, BOTTOM: 5 };
  const TOP_POS = window.innerHeight - size - FLOATER_DEFAULT_MARGIN.BOTTOM;
  const LEFT_POS = LEFT_NAV_PANE_WIDTH + FLOATER_DEFAULT_MARGIN.LEFT;
  return { x: LEFT_POS, y: TOP_POS };
}

function usePosition({
  deps = [],
  onMouseDown = noop,
  onMouseMove = noop,
  onMouseUp = noop,
  onClick = noop,
  size
}) {
  const initialPositionRef = useRef();
  const [position, setPosition] = useState(() => getDefaultPosition(size));
  const ref = useRef(null);
  const isDraggingRef = useRef(false);
  const clickTimeRef = useRef();

  useEffect(() => {
    if (ref.current == null) return undefined;
    const targetEl = ref.current;

    function handleMouseDown(e) {
      clickTimeRef.current = new Date();
      onMouseDown(e, position);
      const { left, top } = targetEl.getBoundingClientRect();
      initialPositionRef.current = {
        x: e.clientX - left,
        y: e.clientY - top
      };
      if (targetEl.contains(e.target)) {
        isDraggingRef.current = true;
      }
    }

    function handleMouseUp(e) {
      onMouseUp(e, position);
      isDraggingRef.current = false;
    }

    function handleMouseMove(e) {
      if (!isDraggingRef.current) return;
      onMouseMove(e, position);
      const { x, y } = initialPositionRef.current;
      const left = e.clientX - x;
      const top = e.clientY - y;
      setPosition({ x: left, y: top });
    }

    function _onClick(e) {
      // click event will fire right after mousedown and mouseup, to prevent that we are checking the clicktime
      if (new Date() - clickTimeRef.current < 300) {
        onClick(e, position);
      }
    }
    targetEl.addEventListener('click', _onClick, true);
    document.addEventListener('mousedown', handleMouseDown);
    targetEl.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      targetEl.removeEventListener('click', _onClick);
      document.removeEventListener('mousedown', handleMouseDown);
      targetEl.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [onMouseDown, onMouseMove, onMouseUp, onClick, position, ...deps]);
  return {
    position,
    setPosition,
    ref
  };
}

const PortalRoot = styled.div`
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  z-index: ${INTERCOM_Z_INDEX + 1000};
  pointer-events: none;
`;

const CircularProgress = ({
  size,
  thickness,
  value,
  progressColor,
  trackColor,
  fill = 'transparent'
}) => {
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="flex position-relative">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-valuemax="100"
        aria-valuemin="0"
        aria-valuenow={value}
        role="progressbar"
        style={{
          transform: 'rotate(-90deg)',
          background: fill,
          borderRadius: '50%',
          boxShadow: 'rgba(62, 64, 66, 0.25) -6px 0px 9px'
        }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={thickness}
          stroke={trackColor}
          strokeLinecap="butt"
          style={{ strokeDasharray: circumference, strokeDashoffset: 0 }}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={thickness}
          stroke={progressColor}
          strokeLinecap="butt"
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: `${(
              ((100 - value) / 100) *
              circumference
            ).toFixed(3)}px`
          }}
        />
      </svg>
      <div className="position-absolute flex actr jctr t0 l0 b0 r0">
        <DisplayText element="h2">
          <div className="text-white">{value}%</div>
        </DisplayText>
      </div>
    </div>
  );
};

function ProgressFloater({
  percentage = 0,
  onFloaterClick = noop,
  size = SIZE
}) {
  const user = getCurrentUserHook();
  const intl = useIntl();

  const [savePersonalization] = useMutation(SAVE_PERSONALIZATION, {
    client: AccountClient
  });

  const { loading, data } = useQuery(GET_PERSONALIZATION_BY_NAME, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    variables: {
      userId: user.userId,
      category: 'onboardingProgressFloater',
      name: 'onboarding'
    }
  });

  const onMouseUp = useCallback(
    (_, pos) => {
      savePersonalization({
        variables: {
          name: 'onboarding',
          category: 'onboardingProgressFloater',
          user: {
            firstName: user?.firstName,
            userId: user?.userId,
            email: user?.email,
            lastName: user?.lastName
          },
          value: JSON.stringify(pos)
        }
      });
    },
    [savePersonalization]
  );

  const { position, setPosition, ref } = usePosition({
    deps: [loading],
    onClick: onFloaterClick,
    onMouseUp,
    size
  });

  useEffect(() => {
    if (data) {
      const resp = data.getPersonalizationByCategoryAndName;
      if (resp != null) {
        const pos = JSON.parse(resp.value);
        setPosition(pos);
      }
    }
  }, [data]);

  if (loading) return null;

  return (
    <Tippy
      content={
        <div className="p10">
          {getTranslatedText(intl, 'product.onboarding.floater.subtitle')}
        </div>
      }
      duration={250}
      delay={500}
      placement="bottom-start"
      arrow={false}
      allowHTML
      theme="dark"
      appendTo={document.body}
    >
      <div
        ref={ref}
        style={{
          top: position.y,
          left: position.x,
          width: size,
          height: size,
          position: 'absolute',
          touchAction: 'none',
          pointerEvents: 'auto',
          color: '#fff',
          transform: `translate3d(${position.x}px, ${position.y}px)`,
          userSelect: 'none',
          cursor: 'move'
        }}
      >
        <CircularProgress
          value={percentage}
          size={size}
          trackColor="#EDEDED"
          progressColor="#1F2C8F"
          fill="linear-gradient(180deg, #7282FF 0%, #3643A5 100%) "
          thickness={8}
        />
      </div>
    </Tippy>
  );
}

export function OnboardingProgressFloaterPortal(props) {
  return ReactDOM.createPortal(
    <PortalRoot>
      <ProgressFloater {...props} />
    </PortalRoot>,
    document.body
  );
}
